import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface PosmProjectPage {
    content: PosmProject[];
    totalElements: number;
}
export interface LocationMatchCandidate {
    id: number; name: string; reference?: string; address?: string; city?: string; retailer?: string; similarity: number; fingerprint: string;
}
export interface LocationMatchPreview {
    candidates: LocationMatchCandidate[]; exactLocationId?: number; requiresReview: boolean; fingerprint: string;
}
export interface PosmLocationReviewDetails {
    code: 'POSM_LOCATION_REVIEW_REQUIRED';
    stores: { storeIndex: number; storeNumber?: string; storeName?: string; message: string; preview: LocationMatchPreview }[];
}
import { APIListWrapper } from '../models/common/api-list-wrapper.model';
import {
    PosmAccountingPayrollLinkRequest,
    PosmAddonCostRequest,
    PosmClientTrackingShare,
    PosmClientTrackingView,
    PosmCloseProjectRequest,
    PosmClosureStatus,
    PosmCostApprovalRequest,
    PosmCostingSummary,
    PosmCompletionReport,
    PosmExternalLaborApprovalRequest,
    PosmFinanceSummary,
    PosmMaterialReservationCommand,
    PosmMaterialReservationRequest,
    PosmGenerateCompletionReportRequest,
    PosmOutsideTruckRateCard,
    PosmOwnTruckRateCard,
    PosmProject,
    PosmProjectDeleteEligibility,
    PosmProjectAttachment,
    PosmProjectSummary,
    PosmReportAvailability,
    PosmReportExport,
    PosmShareClientTrackingRequest,
    PosmStockAvailability,
    PosmStockReservation,
    PosmStockReservationRequest,
    PosmTripCost,
    PosmWorkspaceSummary
} from './models/posm-project.model';
import { PosmProjectItem, PosmProjectItemSource } from './models/posm-project-item.model';
import {
    PosmApprovalRequest,
    PosmDeliverySchedule,
    PosmDeliveryScheduleQuantitySummary,
    PosmLinkedReleaseDelivery,
    PosmDeliveryScheduleImportPreview,
    PosmDeliveryScheduleAllocation,
    PosmQuantityCorrectionPreview,
    PosmQuantityCorrectionRequest,
    PosmQuantityCorrectionResult,
    PosmDeliveryScheduleAutoAllocateRequest,
    PosmDeliveryScheduleAutoAllocateResult,
    PosmDeliveryScheduleFilterCriteria,
    PosmDeliveryScheduleFilterOption,
    PosmLocationReuseCandidate,
    PosmDeliveryScheduleRemovalReleaseRequest,
    PosmDeliveryScheduleReleaseRequest,
    PosmDeliveryScheduleReturnOutcomeCompletionRequest,
    PosmDeliveryNote,
    PosmDeliveryNoteGenerateRequest,
    PosmExecutionCompletionReport,
    PosmExecutionCompletionDeliveryEvidence,
    PosmExecutionCompletionDeliveryEvidenceReviewRequest,
    PosmCompletionEvidenceReviewHistory,
    PosmExecutionCompletionReportGenerateRequest,
    PosmExecutionCompletionReportJob,
    PosmExecutionCompletionReportSummary,
    PosmExecutionCompletionTaskReconciliation,
    PosmExecutionReport,
    PosmInstallationTask,
    PosmInstallationTaskCompletion,
    PosmInstallationTaskEvidence,
    PosmProjectStore,
    PosmRefurbishmentApprovalDecisionRequest,
    PosmRefurbishmentAssetUploadRequest,
    PosmRefurbishmentCollectionStatusRequest,
    PosmRefurbishmentCostApprovalRequest,
    PosmRefurbishmentCostUpdateRequest,
    PosmRefurbishmentEvidence,
    PosmRefurbishmentStageUpdateRequest,
    PosmRefurbishmentTask,
    PosmRouteTask,
    PosmScheduleCompletionReport,
    PosmScheduleCompletionReportReadiness,
    PosmShipmentDeliveryLink,
    PosmStoreChangeApprovalRequest,
    PosmStoreChangeRequest,
    PosmStoreChangeSubmitRequest,
    PosmStrategyWorkGenerateRequest,
    PosmTaskCompletionRequest,
    PosmTaskEvidenceRequest,
    PosmTaskFieldConfig
} from './models/posm-execution.models';
import { WarehousingItem } from '../warehousing/warehousing.model';
import {
    PosmImportCommitRequest,
    PosmImportCommitResult,
    PosmImportPreview,
    PosmPlanningData,
    PosmScoreCards,
    PosmSetupData,
    PosmStrategy,
    PosmStrategyItem
} from './models/posm-planning.model';

@Injectable({
    providedIn: 'root'
})
export class PosmService {
    private http = inject(HttpClient);
    private readonly apiUrl = `${environment.restApiUrl}posm`;
    private readonly executionApiUrl = `${environment.restApiUrl}posm/execution`;
    private readonly planningApiUrl = `${environment.restApiUrl}posm/planning`;
    private readonly refurbishmentApiUrl = `${environment.restApiUrl}posm/refurbishment`;
    private readonly costingApiUrl = `${environment.restApiUrl}posm/costing`;
    private readonly closureApiUrl = `${environment.restApiUrl}posm/closure`;

    getProjectsPage(owningCompanyId: number, page: number, size: number, filters: {
        search: string; status: string | null; priority: string | null;
        jobType: string | null; client: string | null; brand: string | null;
    }): Observable<PosmProjectPage> {
        let params = new HttpParams().set('owningCompanyId', owningCompanyId).set('page', page).set('size', size);
        Object.entries(filters).forEach(([key, value]) => {
            if (value?.trim()) params = params.set(key, value.trim());
        });
        return this.http.get<PosmProjectPage>(`${this.apiUrl}/projects/page`, { params });
    }

    getProject(id: number, owningCompanyId: number): Observable<PosmProject> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmProject>(`${this.apiUrl}/projects/${id}`, { params });
    }

    createProject(project: PosmProject): Observable<PosmProject> {
        return this.http.post<PosmProject>(`${this.apiUrl}/projects`, project);
    }

    updateProjectHeader(id: number, owningCompanyId: number, header: PosmProject): Observable<PosmProject> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.put<PosmProject>(`${this.apiUrl}/projects/${id}/header`, header, { params });
    }

    getProjectDeleteEligibility(id: number, owningCompanyId: number): Observable<PosmProjectDeleteEligibility> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmProjectDeleteEligibility>(`${this.apiUrl}/projects/${id}/delete-eligibility`, { params });
    }

    deleteProject(id: number, owningCompanyId: number): Observable<void> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.delete<void>(`${this.apiUrl}/projects/${id}`, { params });
    }

    downloadTemplate(): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/projects/template`, { responseType: 'blob' });
    }

    exportDeliverySchedules(projectId: number, owningCompanyId: number): Observable<Blob> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get(`${this.apiUrl}/projects/${projectId}/delivery-schedules/export`, {
            params,
            responseType: 'blob'
        });
    }

    parseStoreList(file: File): Observable<PosmDeliverySchedule[]> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<PosmDeliverySchedule[]>(`${this.apiUrl}/projects/parse-store-list`, formData);
    }

    parseDeliverySchedule(file: File, owningCompanyId: number): Observable<PosmDeliveryScheduleImportPreview> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmDeliveryScheduleImportPreview>(`${this.apiUrl}/projects/parse-delivery-schedule`, formData);
    }

    previewLocationMatches(companyId: number, inputs: { name?: string; reference?: string; city?: string; retailer?: string; storeNumber?: string }[]): Observable<LocationMatchPreview[]> {
        const params = new HttpParams().set('owningCompanyId', companyId.toString());
        return this.http.post<LocationMatchPreview[]>(`${environment.restApiUrl}location-matches/preview`, inputs, { params });
    }

    previewProjectScheduleImport(projectId: number, file: File, owningCompanyId: number): Observable<PosmDeliveryScheduleImportPreview> {
        const formData = new FormData();
        formData.append('file', file);
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmDeliveryScheduleImportPreview>(`${this.apiUrl}/projects/${projectId}/delivery-schedule/import-preview`, formData, { params });
    }

    importProjectSchedules(projectId: number, owningCompanyId: number, additions: PosmProject): Observable<void> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<void>(`${this.apiUrl}/projects/${projectId}/delivery-schedule/import`, additions, { params });
    }

    seedPlanningDefaults(owningCompanyId: number): Observable<void> {
        return this.http.post<void>(`${this.planningApiUrl}/setup/seed-defaults`, {}, {
            params: new HttpParams().set('owningCompanyId', owningCompanyId.toString())
        });
    }

    getSetup(type: string, owningCompanyId: number, activeOnly = true): Observable<PosmSetupData[]> {
        const params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('activeOnly', String(activeOnly));
        return this.http.get<PosmSetupData[]>(`${this.planningApiUrl}/setup/${type}`, { params });
    }

    saveSetup(data: PosmSetupData): Observable<PosmSetupData> {
        return this.http.post<PosmSetupData>(`${this.planningApiUrl}/setup`, data);
    }

    getProjectPlanning(projectId: number, owningCompanyId: number): Observable<PosmPlanningData> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmPlanningData>(`${this.planningApiUrl}/projects/${projectId}`, { params });
    }

    getScoreCards(projectId: number, owningCompanyId: number): Observable<PosmScoreCards> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmScoreCards>(`${this.planningApiUrl}/projects/${projectId}/scorecards`, { params });
    }

    getStrategies(projectId: number, owningCompanyId: number): Observable<PosmStrategy[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmStrategy[]>(`${this.planningApiUrl}/projects/${projectId}/strategies`, { params });
    }

    saveStrategy(strategy: PosmStrategy): Observable<PosmStrategy> {
        return this.http.post<PosmStrategy>(`${this.planningApiUrl}/strategies`, this.normalizeDates(strategy));
    }

    saveStrategyItem(item: PosmStrategyItem): Observable<PosmStrategyItem> {
        return this.http.post<PosmStrategyItem>(`${this.planningApiUrl}/strategy-items`, item);
    }

    previewPlannerImport(file: File, owningCompanyId: number, jobTypeId?: number): Observable<PosmImportPreview> {
        const formData = new FormData();
        formData.append('file', file);
        let params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        if (jobTypeId) params = params.set('jobTypeId', jobTypeId.toString());
        return this.http.post<PosmImportPreview>(`${this.planningApiUrl}/imports/preview`, formData, { params });
    }

    commitPlannerImport(request: PosmImportCommitRequest): Observable<PosmImportCommitResult> {
        return this.http.post<PosmImportCommitResult>(`${this.planningApiUrl}/imports/commit`, request);
    }

    getWarehousingItem(id: number, owningCompanyId: number): Observable<WarehousingItem> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<WarehousingItem>(`${this.executionApiUrl}/warehousing-items/${id}`, { params });
    }

    getStoresByProject(projectId: number, owningCompanyId: number): Observable<PosmProjectStore[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmProjectStore[]>(`${this.executionApiUrl}/projects/${projectId}/stores`, { params });
    }

    getShipmentDeliveryLinksByProject(projectId: number, owningCompanyId: number): Observable<PosmShipmentDeliveryLink[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmShipmentDeliveryLink[]>(`${this.executionApiUrl}/projects/${projectId}/shipment-delivery-links`, { params });
    }

    getRouteTask(id: number, owningCompanyId: number): Observable<PosmRouteTask> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmRouteTask>(`${this.executionApiUrl}/route-tasks/${id}`, { params });
    }

    saveInstallationTask(task: PosmInstallationTask): Observable<PosmInstallationTask> {
        return this.http.post<PosmInstallationTask>(`${this.executionApiUrl}/installation-tasks`, task);
    }

    getFieldConfigsByProject(projectId: number, owningCompanyId: number): Observable<PosmTaskFieldConfig[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmTaskFieldConfig[]>(`${this.executionApiUrl}/projects/${projectId}/field-configs`, { params });
    }

    getRefurbTasksByProject(projectId: number, companyId?: number): Observable<PosmRefurbishmentTask[]> {
        let params = new HttpParams();
        if (companyId) params = params.set('companyId', companyId.toString());
        return this.http.get<PosmRefurbishmentTask[]>(`${this.refurbishmentApiUrl}/tasks/project/${projectId}`, { params });
    }

    createRefurbTask(task: PosmRefurbishmentTask, companyId: number): Observable<PosmRefurbishmentTask[]> {
        const params = new HttpParams().set('companyId', companyId.toString());
        return this.http.post<PosmRefurbishmentTask[]>(`${this.refurbishmentApiUrl}/task`, task, { params });
    }

    generateStrategyWork(request: PosmStrategyWorkGenerateRequest, companyId: number): Observable<PosmRefurbishmentTask[]> {
        const params = new HttpParams().set('companyId', companyId.toString());
        return this.http.post<PosmRefurbishmentTask[]>(`${this.refurbishmentApiUrl}/tasks/generate`, this.normalizeDates(request), { params });
    }

    approveRefurbTask(taskId: number): Observable<any> {
        const params = new HttpParams().set('taskId', taskId.toString());
        return this.http.post<any>(`${this.refurbishmentApiUrl}/approve`, {}, { params });
    }

    addRefurbLog(taskId: number, log: any): Observable<any> {
        const params = new HttpParams()
            .set('taskId', taskId.toString())
            .set('stage', log.stage)
            .set('notes', log.notes || '')
            .set('cost', log.cost || 0)
            .set('photoUrl', log.photoUrl || '');
        return this.http.post<any>(`${this.refurbishmentApiUrl}/log`, {}, { params });
    }

    approveTaskCost(taskId: number, cost: number): Observable<any> {
        const params = new HttpParams()
            .set('taskId', taskId.toString())
            .set('cost', cost.toString());
        return this.http.post<any>(`${this.refurbishmentApiUrl}/approve-cost`, {}, { params });
    }

    approveRefurbTaskCost(taskId: number, companyId: number, request: PosmRefurbishmentCostApprovalRequest): Observable<PosmRefurbishmentTask> {
        const params = new HttpParams().set('companyId', companyId.toString());
        return this.http.post<PosmRefurbishmentTask>(`${this.refurbishmentApiUrl}/tasks/${taskId}/cost-approval`, request, { params });
    }

    requestRefurbTaskCostUpdate(taskId: number, companyId: number, request: PosmRefurbishmentCostUpdateRequest): Observable<PosmRefurbishmentTask> {
        const params = new HttpParams().set('companyId', companyId.toString());
        return this.http.post<PosmRefurbishmentTask>(`${this.refurbishmentApiUrl}/tasks/${taskId}/cost-updates`, request, { params });
    }

    getRefurbTaskEvidence(taskId: number, companyId: number): Observable<PosmRefurbishmentEvidence[]> {
        const params = new HttpParams().set('companyId', companyId.toString());
        return this.http.get<PosmRefurbishmentEvidence[]>(`${this.refurbishmentApiUrl}/tasks/${taskId}/evidence`, { params });
    }

    addRefurbTaskEvidence(taskId: number, companyId: number, evidenceType: string, request: PosmRefurbishmentAssetUploadRequest): Observable<PosmRefurbishmentEvidence> {
        const params = new HttpParams()
            .set('companyId', companyId.toString())
            .set('evidenceType', evidenceType);
        return this.http.post<PosmRefurbishmentEvidence>(`${this.refurbishmentApiUrl}/tasks/${taskId}/evidence`, request, { params });
    }

    uploadRefurbTaskArtwork(taskId: number, companyId: number, request: PosmRefurbishmentAssetUploadRequest): Observable<PosmRefurbishmentTask> {
        const params = new HttpParams().set('companyId', companyId.toString());
        return this.http.post<PosmRefurbishmentTask>(`${this.refurbishmentApiUrl}/tasks/${taskId}/artwork`, request, { params });
    }

    uploadRefurbTaskMockup(taskId: number, companyId: number, request: PosmRefurbishmentAssetUploadRequest): Observable<PosmRefurbishmentTask> {
        const params = new HttpParams().set('companyId', companyId.toString());
        return this.http.post<PosmRefurbishmentTask>(`${this.refurbishmentApiUrl}/tasks/${taskId}/mockup`, request, { params });
    }

    approveRefurbTaskMockup(taskId: number, companyId: number, request: PosmRefurbishmentApprovalDecisionRequest): Observable<PosmRefurbishmentTask> {
        const params = new HttpParams().set('companyId', companyId.toString());
        return this.http.post<PosmRefurbishmentTask>(`${this.refurbishmentApiUrl}/tasks/${taskId}/mockup/approve`, request, { params });
    }

    rejectRefurbTaskMockup(taskId: number, companyId: number, request: PosmRefurbishmentApprovalDecisionRequest): Observable<PosmRefurbishmentTask> {
        const params = new HttpParams().set('companyId', companyId.toString());
        return this.http.post<PosmRefurbishmentTask>(`${this.refurbishmentApiUrl}/tasks/${taskId}/mockup/reject`, request, { params });
    }

    updateRefurbTaskStage(taskId: number, companyId: number, request: PosmRefurbishmentStageUpdateRequest): Observable<PosmRefurbishmentTask> {
        const params = new HttpParams().set('companyId', companyId.toString());
        return this.http.post<PosmRefurbishmentTask>(`${this.refurbishmentApiUrl}/tasks/${taskId}/stage`, this.normalizeDates(request), { params });
    }

    updateRefurbTaskCollection(taskId: number, companyId: number, request: PosmRefurbishmentCollectionStatusRequest): Observable<PosmRefurbishmentTask> {
        const params = new HttpParams().set('companyId', companyId.toString());
        return this.http.post<PosmRefurbishmentTask>(`${this.refurbishmentApiUrl}/tasks/${taskId}/collection`, request, { params });
    }

    assignJobToTrip(taskId: number, shipmentId: number): Observable<any> {
        const params = new HttpParams()
            .set('taskId', taskId.toString())
            .set('shipmentId', shipmentId.toString());
        return this.http.post<any>(`${this.refurbishmentApiUrl}/assign-to-trip`, {}, { params });
    }

    getExecutionTasksByProject(projectId: number, owningCompanyId?: number): Observable<PosmInstallationTask[]> {
        let params = new HttpParams();
        if (owningCompanyId) params = params.set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmInstallationTask[]>(`${this.executionApiUrl}/tasks/project/${projectId}`, { params });
    }

    completeInstallationTask(taskId: number, data: PosmInstallationTaskCompletion): Observable<PosmInstallationTask> {
        const params = new HttpParams()
            .set('lat', data.lat.toString())
            .set('lng', data.lng.toString())
            .set('photoUrl', data.photoUrl)
            .set('signatureUrl', data.signatureUrl)
            .set('remarks', data.remarks || '');
        return this.http.post<PosmInstallationTask>(`${this.executionApiUrl}/tasks/${taskId}/complete`, {}, { params });
    }

    completeInstallationTaskWorkflow(taskId: number, owningCompanyId: number, data: PosmTaskCompletionRequest): Observable<PosmInstallationTask> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmInstallationTask>(`${this.executionApiUrl}/tasks/${taskId}/complete-workflow`, this.normalizeDates(data), { params });
    }

    getInstallationTaskEvidence(taskId: number, owningCompanyId: number): Observable<PosmInstallationTaskEvidence[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmInstallationTaskEvidence[]>(`${this.executionApiUrl}/tasks/${taskId}/evidence`, { params });
    }

    addInstallationTaskEvidence(taskId: number, owningCompanyId: number, request: PosmTaskEvidenceRequest): Observable<PosmInstallationTaskEvidence> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmInstallationTaskEvidence>(`${this.executionApiUrl}/tasks/${taskId}/evidence`, request, { params });
    }

    approveInstallationTaskEvidence(evidenceId: number, owningCompanyId: number, request: PosmApprovalRequest): Observable<PosmInstallationTaskEvidence> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmInstallationTaskEvidence>(`${this.executionApiUrl}/task-evidence/${evidenceId}/approve`, request, { params });
    }

    rejectInstallationTaskEvidence(evidenceId: number, owningCompanyId: number, request: PosmApprovalRequest): Observable<PosmInstallationTaskEvidence> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmInstallationTaskEvidence>(`${this.executionApiUrl}/task-evidence/${evidenceId}/reject`, request, { params });
    }

    approveInstallationTaskDelay(taskId: number, owningCompanyId: number, request: PosmApprovalRequest): Observable<PosmInstallationTask> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmInstallationTask>(`${this.executionApiUrl}/tasks/${taskId}/delay/approve`, request, { params });
    }

    rejectInstallationTaskDelay(taskId: number, owningCompanyId: number, request: PosmApprovalRequest): Observable<PosmInstallationTask> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmInstallationTask>(`${this.executionApiUrl}/tasks/${taskId}/delay/reject`, request, { params });
    }

    generateDeliveryNote(deliveryId: number, payloadJson: string): Observable<void> {
        return this.http.post<void>(`${this.executionApiUrl}/delivery-note/${deliveryId}`, payloadJson);
    }

    getDeliveryNotesByProject(projectId: number, owningCompanyId: number): Observable<PosmDeliveryNote[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmDeliveryNote[]>(`${this.executionApiUrl}/projects/${projectId}/delivery-notes`, { params });
    }

    generateTaskDeliveryNote(owningCompanyId: number, request: PosmDeliveryNoteGenerateRequest): Observable<PosmDeliveryNote> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmDeliveryNote>(`${this.executionApiUrl}/delivery-notes/generate`, this.normalizeDates(request), { params });
    }

    getStoreChangeRequests(projectId: number, owningCompanyId: number): Observable<PosmStoreChangeRequest[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmStoreChangeRequest[]>(`${this.executionApiUrl}/projects/${projectId}/store-change-requests`, { params });
    }

    requestStoreChange(projectId: number, owningCompanyId: number, request: PosmStoreChangeSubmitRequest): Observable<PosmStoreChangeRequest> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmStoreChangeRequest>(`${this.executionApiUrl}/projects/${projectId}/store-change-requests`, this.normalizeDates(request), { params });
    }

    approveStoreChangeRequest(changeId: number, owningCompanyId: number, approvalType: 'OPS' | 'SALES', request: PosmStoreChangeApprovalRequest): Observable<PosmStoreChangeRequest> {
        const params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('approvalType', approvalType);
        return this.http.post<PosmStoreChangeRequest>(`${this.executionApiUrl}/store-change-requests/${changeId}/approve`, request, { params });
    }

    rejectStoreChangeRequest(changeId: number, owningCompanyId: number, approvalType: 'OPS' | 'SALES', request: PosmStoreChangeApprovalRequest): Observable<PosmStoreChangeRequest> {
        const params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('approvalType', approvalType);
        return this.http.post<PosmStoreChangeRequest>(`${this.executionApiUrl}/store-change-requests/${changeId}/reject`, request, { params });
    }

    generateExecutionReport(projectId: number, owningCompanyId: number, reportType: string, generatedByCustomerId?: number): Observable<PosmExecutionReport> {
        let params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('reportType', reportType);
        if (generatedByCustomerId) params = params.set('generatedByCustomerId', generatedByCustomerId.toString());
        return this.http.post<PosmExecutionReport>(`${this.executionApiUrl}/projects/${projectId}/execution-reports/generate`, {}, { params });
    }

    getExecutionCompletionReportSummary(projectId: number, owningCompanyId: number): Observable<PosmExecutionCompletionReportSummary> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmExecutionCompletionReportSummary>(
            `${this.executionApiUrl}/projects/${projectId}/completion-reports/summary`, { params });
    }

    getExecutionCompletionReports(projectId: number, owningCompanyId: number): Observable<PosmExecutionCompletionReport[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmExecutionCompletionReport[]>(
            `${this.executionApiUrl}/projects/${projectId}/completion-reports`, { params });
    }

    getExecutionCompletionDeliveryEvidence(projectId: number, owningCompanyId: number): Observable<PosmExecutionCompletionDeliveryEvidence[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmExecutionCompletionDeliveryEvidence[]>(
            `${this.executionApiUrl}/projects/${projectId}/completion-reports/delivery-evidence`, { params });
    }

    reviewExecutionCompletionDeliveryEvidence(projectId: number, owningCompanyId: number, sourceType: string,
        sourceId: number, request: PosmExecutionCompletionDeliveryEvidenceReviewRequest): Observable<PosmExecutionCompletionDeliveryEvidence> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmExecutionCompletionDeliveryEvidence>(
            `${this.executionApiUrl}/projects/${projectId}/completion-reports/delivery-evidence/${encodeURIComponent(sourceType)}/${sourceId}/review`,
            request, { params });
    }

    getCompletionEvidenceReviewHistory(projectId: number, owningCompanyId: number, scheduleId: number,
        sourceType: string, sourceId: number): Observable<PosmCompletionEvidenceReviewHistory[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString())
            .set('deliveryScheduleId', scheduleId.toString());
        return this.http.get<PosmCompletionEvidenceReviewHistory[]>(
            `${this.executionApiUrl}/projects/${projectId}/completion-reports/delivery-evidence/${encodeURIComponent(sourceType)}/${sourceId}/history`, { params });
    }

    reconcileExecutionCompletionTasks(projectId: number, owningCompanyId: number): Observable<PosmExecutionCompletionTaskReconciliation> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmExecutionCompletionTaskReconciliation>(
            `${this.executionApiUrl}/projects/${projectId}/completion-reports/reconcile-delivery-tasks`, {}, { params });
    }

    generateExecutionCompletionReport(projectId: number, owningCompanyId: number,
        request: PosmExecutionCompletionReportGenerateRequest = {}): Observable<PosmExecutionCompletionReport> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmExecutionCompletionReport>(
            `${this.executionApiUrl}/projects/${projectId}/completion-reports`, request, { params });
    }

    createExecutionCompletionReportJob(projectId: number, owningCompanyId: number,
        request: PosmExecutionCompletionReportGenerateRequest): Observable<PosmExecutionCompletionReportJob> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmExecutionCompletionReportJob>(
            `${this.executionApiUrl}/projects/${projectId}/completion-report-jobs`, request, { params });
    }

    latestExecutionCompletionReportJob(projectId: number, owningCompanyId: number): Observable<PosmExecutionCompletionReportJob | null> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmExecutionCompletionReportJob | null>(
            `${this.executionApiUrl}/projects/${projectId}/completion-report-jobs/latest`, { params });
    }

    getExecutionCompletionReportJob(projectId: number, jobId: number, owningCompanyId: number): Observable<PosmExecutionCompletionReportJob> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmExecutionCompletionReportJob>(
            `${this.executionApiUrl}/projects/${projectId}/completion-report-jobs/${jobId}`, { params });
    }

    downloadExecutionCompletionReport(projectId: number, reportId: number, owningCompanyId: number): Observable<Blob> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get(`${this.executionApiUrl}/projects/${projectId}/completion-reports/${reportId}/download`,
            { params, responseType: 'blob' });
    }

    getProjectSummary(projectId: number, owningCompanyId: number): Observable<PosmProjectSummary> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmProjectSummary>(`${this.apiUrl}/projects/${projectId}/summary`, { params });
    }

    getProjectSummaries(projectIds: number[], owningCompanyId: number): Observable<PosmProjectSummary[]> {
        let params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        projectIds.forEach(projectId => {
            params = params.append('projectIds', projectId.toString());
        });
        return this.http.get<PosmProjectSummary[]>(`${this.apiUrl}/projects/summaries`, { params });
    }

    getWorkspaceSummary(projectId: number, owningCompanyId: number): Observable<PosmWorkspaceSummary> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmWorkspaceSummary>(`${this.apiUrl}/projects/${projectId}/workspace-summary`, { params });
    }

    getFinanceSummary(projectId: number, owningCompanyId: number): Observable<PosmFinanceSummary> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmFinanceSummary>(`${this.apiUrl}/projects/${projectId}/finance-summary`, { params });
    }

    getPosmCostingSummary(projectId: number, owningCompanyId: number): Observable<PosmCostingSummary> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmCostingSummary>(`${this.costingApiUrl}/projects/${projectId}/summary`, { params });
    }

    recalculatePosmCosts(projectId: number, owningCompanyId: number, planId?: number, releaseId?: number): Observable<PosmCostingSummary> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmCostingSummary>(`${this.costingApiUrl}/projects/${projectId}/recalculate`, { planId, releaseId }, { params });
    }

    getOwnTruckRateCards(owningCompanyId: number): Observable<PosmOwnTruckRateCard[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmOwnTruckRateCard[]>(`${this.costingApiUrl}/own-truck-rate-cards`, { params });
    }

    saveOwnTruckRateCard(rateCard: PosmOwnTruckRateCard): Observable<PosmOwnTruckRateCard> {
        return this.http.post<PosmOwnTruckRateCard>(`${this.costingApiUrl}/own-truck-rate-cards`, rateCard);
    }

    getOutsideTruckRateCards(owningCompanyId: number): Observable<PosmOutsideTruckRateCard[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmOutsideTruckRateCard[]>(`${this.costingApiUrl}/outside-truck-rate-cards`, { params });
    }

    saveOutsideTruckRateCard(rateCard: PosmOutsideTruckRateCard): Observable<PosmOutsideTruckRateCard> {
        return this.http.post<PosmOutsideTruckRateCard>(`${this.costingApiUrl}/outside-truck-rate-cards`, rateCard);
    }

    requestPosmAddonCost(tripCostId: number, owningCompanyId: number, request: PosmAddonCostRequest): Observable<PosmTripCost> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmTripCost>(`${this.costingApiUrl}/trip-costs/${tripCostId}/addon-request`, request, { params });
    }

    approvePosmAddonCost(tripCostId: number, owningCompanyId: number, request: PosmCostApprovalRequest): Observable<PosmTripCost> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmTripCost>(`${this.costingApiUrl}/trip-costs/${tripCostId}/addon-approval`, request, { params });
    }

    updatePosmExternalLaborApproval(tripCostId: number, owningCompanyId: number, request: PosmExternalLaborApprovalRequest): Observable<PosmTripCost> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmTripCost>(`${this.costingApiUrl}/trip-costs/${tripCostId}/external-labor-approval`, request, { params });
    }

    linkPosmAccountingPayroll(tripCostId: number, owningCompanyId: number, request: PosmAccountingPayrollLinkRequest): Observable<PosmTripCost> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmTripCost>(`${this.costingApiUrl}/trip-costs/${tripCostId}/posting-link`, request, { params });
    }

    getReportAvailability(projectId: number, owningCompanyId: number): Observable<PosmReportAvailability> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmReportAvailability>(`${this.apiUrl}/projects/${projectId}/reports/availability`, { params });
    }

    getProjectAttachments(projectId: number, owningCompanyId: number): Observable<PosmProjectAttachment[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmProjectAttachment[]>(`${this.apiUrl}/projects/${projectId}/attachments`, { params });
    }

    saveProjectAttachment(projectId: number, owningCompanyId: number, attachment: PosmProjectAttachment): Observable<PosmProjectAttachment> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmProjectAttachment>(`${this.apiUrl}/projects/${projectId}/attachments`, attachment, { params });
    }

    deleteProjectAttachment(projectId: number, attachmentId: number, owningCompanyId: number): Observable<void> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.delete<void>(`${this.apiUrl}/projects/${projectId}/attachments/${attachmentId}`, { params });
    }

    getProjectItems(projectId: number, owningCompanyId: number): Observable<PosmProjectItem[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmProjectItem[]>(`${this.apiUrl}/projects/${projectId}/items`, { params });
    }

    getProjectStores(projectId: number, owningCompanyId: number): Observable<PosmProjectStore[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmProjectStore[]>(`${this.apiUrl}/projects/${projectId}/stores`, { params });
    }

    getDeliverySchedules(projectId: number, owningCompanyId: number): Observable<PosmDeliverySchedule[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmDeliverySchedule[]>(`${this.apiUrl}/projects/${projectId}/delivery-schedule`, { params });
    }

    getLinkedReleaseDeliveries(projectId: number, owningCompanyId: number): Observable<PosmLinkedReleaseDelivery[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmLinkedReleaseDelivery[]>(`${this.executionApiUrl}/projects/${projectId}/linked-deliveries`, { params });
    }

    getDeliverySchedulesPage(projectId: number, owningCompanyId: number, page: number, pageSize: number,
        filters?: PosmDeliveryScheduleFilterCriteria): Observable<APIListWrapper<PosmDeliverySchedule>> {
        let params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('page', Math.max(page, 0).toString())
            .set('pageSize', Math.max(pageSize, 1).toString());
        params = this.appendDeliveryScheduleFilters(params, filters);
        return this.http.get<APIListWrapper<PosmDeliverySchedule>>(`${this.apiUrl}/projects/${projectId}/delivery-schedules`, { params });
    }

    getDeliveryScheduleFilterValues(projectId: number, owningCompanyId: number, field: string, query: string,
        page: number, pageSize: number, filters?: PosmDeliveryScheduleFilterCriteria): Observable<APIListWrapper<PosmDeliveryScheduleFilterOption>> {
        let params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('field', field)
            .set('query', query || '')
            .set('page', Math.max(page, 0).toString())
            .set('pageSize', Math.max(pageSize, 1).toString());
        params = this.appendDeliveryScheduleFilters(params, filters);
        return this.http.get<APIListWrapper<PosmDeliveryScheduleFilterOption>>(`${this.apiUrl}/projects/${projectId}/delivery-schedule/filter-values`, { params });
    }

    getAllDeliverySchedules(owningCompanyId: number): Observable<PosmDeliverySchedule[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmDeliverySchedule[]>(`${this.apiUrl}/projects/delivery-schedules`, { params });
    }

    getDeliveryScheduleByLineId(lineId: number, owningCompanyId: number): Observable<PosmDeliverySchedule> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmDeliverySchedule>(`${this.apiUrl}/projects/delivery-schedules/${lineId}`, { params });
    }

    getDeliverySchedule(lineId: number, owningCompanyId: number): Observable<PosmDeliverySchedule> {
        return this.getDeliveryScheduleByLineId(lineId, owningCompanyId);
    }

    getDeliveryScheduleQuantitySummary(projectId: number, itemId: number, owningCompanyId: number): Observable<PosmDeliveryScheduleQuantitySummary> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmDeliveryScheduleQuantitySummary>(
            `${this.apiUrl}/projects/${projectId}/items/${itemId}/scheduled-quantity`, { params });
    }

    updateProjectStore(projectId: number, storeId: number, owningCompanyId: number, store: PosmProjectStore): Observable<PosmProjectStore> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.put<PosmProjectStore>(`${this.apiUrl}/projects/${projectId}/stores/${storeId}`, this.normalizeDates(store), { params });
    }

    updateProjectItem(projectId: number, itemId: number, owningCompanyId: number, item: PosmProjectItem): Observable<PosmProjectItem> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.put<PosmProjectItem>(`${this.apiUrl}/projects/${projectId}/items/${itemId}`, item, { params });
    }

    updateDeliverySchedule(projectId: number, lineId: number, owningCompanyId: number, schedule: PosmDeliverySchedule): Observable<PosmDeliverySchedule> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.put<PosmDeliverySchedule>(`${this.apiUrl}/projects/${projectId}/delivery-schedule/${lineId}`, this.normalizeDates(schedule), { params });
    }

    previewQuantityCorrection(projectId: number, lineId: number, owningCompanyId: number): Observable<PosmQuantityCorrectionPreview> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmQuantityCorrectionPreview>(
            `${this.apiUrl}/projects/${projectId}/delivery-schedule/${lineId}/quantity-correction`, { params });
    }

    applyQuantityCorrection(projectId: number, lineId: number, owningCompanyId: number,
        request: PosmQuantityCorrectionRequest): Observable<PosmQuantityCorrectionResult> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmQuantityCorrectionResult>(
            `${this.apiUrl}/projects/${projectId}/delivery-schedule/${lineId}/quantity-correction`, request, { params });
    }

    createDeliverySchedule(projectId: number, owningCompanyId: number, schedule: PosmDeliverySchedule): Observable<PosmDeliverySchedule> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmDeliverySchedule>(`${this.apiUrl}/projects/${projectId}/delivery-schedule`, this.normalizeDates(schedule), { params });
    }

    getProjectDeliverySchedule(projectId: number, lineId: number, owningCompanyId: number): Observable<PosmDeliverySchedule> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmDeliverySchedule>(`${this.apiUrl}/projects/${projectId}/delivery-schedule/${lineId}`, { params });
    }

    getScheduleCompletionReportReadiness(projectId: number, scheduleId: number,
        owningCompanyId: number): Observable<PosmScheduleCompletionReportReadiness> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmScheduleCompletionReportReadiness>(
            `${this.apiUrl}/projects/${projectId}/delivery-schedule/${scheduleId}/completion-report/readiness`,
            { params });
    }

    getScheduleCompletionReports(projectId: number, scheduleId: number,
        owningCompanyId: number): Observable<PosmScheduleCompletionReport[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmScheduleCompletionReport[]>(
            `${this.apiUrl}/projects/${projectId}/delivery-schedule/${scheduleId}/completion-reports`,
            { params });
    }

    downloadScheduleCompletionReport(projectId: number, scheduleId: number, reportId: number,
        owningCompanyId: number): Observable<Blob> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get(
            `${this.apiUrl}/projects/${projectId}/delivery-schedule/${scheduleId}/completion-reports/${reportId}/download`,
            { params, responseType: 'blob' });
    }

    getDeliveryScheduleLocationReuseCandidates(projectId: number, lineId: number, owningCompanyId: number,
        page = 0, pageSize = 5): Observable<APIListWrapper<PosmLocationReuseCandidate>> {
        const params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('page', page.toString())
            .set('pageSize', pageSize.toString());
        return this.http.get<APIListWrapper<PosmLocationReuseCandidate>>(
            `${this.apiUrl}/projects/${projectId}/delivery-schedule/${lineId}/location-reuse-candidates`,
            { params });
    }

    getDeliveryScheduleAllocations(projectId: number, lineId: number, owningCompanyId: number): Observable<PosmDeliveryScheduleAllocation[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmDeliveryScheduleAllocation[]>(`${this.apiUrl}/projects/${projectId}/delivery-schedule/${lineId}/allocations`, { params });
    }

    getDeliveryScheduleAllocationCandidates(projectId: number, lineId: number, owningCompanyId: number): Observable<PosmDeliveryScheduleAllocation[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmDeliveryScheduleAllocation[]>(`${this.apiUrl}/projects/${projectId}/delivery-schedule/${lineId}/allocation-candidates`, { params });
    }

    createDeliveryScheduleAllocation(projectId: number, lineId: number, owningCompanyId: number, allocation: PosmDeliveryScheduleAllocation): Observable<PosmDeliveryScheduleAllocation> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmDeliveryScheduleAllocation>(`${this.apiUrl}/projects/${projectId}/delivery-schedule/${lineId}/allocations`, allocation, { params });
    }

    deleteDeliveryScheduleAllocation(projectId: number, lineId: number, allocationId: number, owningCompanyId: number): Observable<void> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.delete<void>(`${this.apiUrl}/projects/${projectId}/delivery-schedule/${lineId}/allocations/${allocationId}`, { params });
    }

    autoAllocateDeliverySchedules(projectId: number, owningCompanyId: number,
        request: PosmDeliveryScheduleAutoAllocateRequest): Observable<PosmDeliveryScheduleAutoAllocateResult> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmDeliveryScheduleAutoAllocateResult>(
            `${this.apiUrl}/projects/${projectId}/delivery-schedule/auto-allocate`,
            request || {},
            { params });
    }

    previewAutoAllocateDeliverySchedules(projectId: number, owningCompanyId: number,
        request: PosmDeliveryScheduleAutoAllocateRequest): Observable<PosmDeliveryScheduleAutoAllocateResult> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmDeliveryScheduleAutoAllocateResult>(
            `${this.apiUrl}/projects/${projectId}/delivery-schedule/auto-allocate/preview`,
            request || {},
            { params });
    }

    generateInstallationTasksFromSchedule(projectId: number, owningCompanyId: number): Observable<PosmDeliverySchedule[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmDeliverySchedule[]>(`${this.apiUrl}/projects/${projectId}/delivery-schedule/generate-installation-tasks`, {}, { params });
    }

    generateOrderReleasesFromSchedule(projectId: number, owningCompanyId: number, request: PosmDeliveryScheduleReleaseRequest): Observable<PosmDeliverySchedule[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmDeliverySchedule[]>(`${this.apiUrl}/projects/${projectId}/delivery-schedule/generate-order-releases`, this.normalizeDates(request || {}), { params });
    }

    generateRemovalOrderReleasesFromSchedule(projectId: number, owningCompanyId: number, request: PosmDeliveryScheduleRemovalReleaseRequest): Observable<PosmDeliverySchedule[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmDeliverySchedule[]>(
            `${this.apiUrl}/projects/${projectId}/delivery-schedule/generate-removal-order-releases`,
            this.normalizeDates(request || {}),
            { params });
    }

    completeReturnOutcome(projectId: number, lineId: number, owningCompanyId: number,
        request: PosmDeliveryScheduleReturnOutcomeCompletionRequest): Observable<PosmDeliverySchedule> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmDeliverySchedule>(
            `${this.apiUrl}/projects/${projectId}/delivery-schedule/${lineId}/complete-return-outcome`,
            request || {},
            { params });
    }

    getStockAvailability(owningCompanyId: number, itemId: number, warehouseId: number, conditionId?: number | null): Observable<PosmStockAvailability> {
        let params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('itemId', itemId.toString())
            .set('warehouseId', warehouseId.toString());
        if (conditionId !== null && conditionId !== undefined) {
            params = params.set('conditionId', conditionId.toString());
        }
        return this.http.get<PosmStockAvailability>(`${this.apiUrl}/projects/stock-availability`, { params });
    }

    reserveProjectItemStock(projectId: number, itemId: number, owningCompanyId: number, request: PosmStockReservationRequest): Observable<PosmStockReservation> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmStockReservation>(`${this.apiUrl}/projects/${projectId}/items/${itemId}/reserve-stock`, request, { params });
    }

    getProjectItemSources(projectId: number, itemId: number, owningCompanyId: number): Observable<PosmProjectItemSource[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmProjectItemSource[]>(`${this.apiUrl}/projects/${projectId}/items/${itemId}/sources`, { params });
    }

    createProjectItemSource(projectId: number, itemId: number, owningCompanyId: number, source: PosmProjectItemSource): Observable<PosmProjectItemSource> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmProjectItemSource>(`${this.apiUrl}/projects/${projectId}/items/${itemId}/sources`, this.normalizeDates(source), { params });
    }

    updateProjectItemSource(projectId: number, itemId: number, sourceId: number, owningCompanyId: number, source: PosmProjectItemSource): Observable<PosmProjectItemSource> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.put<PosmProjectItemSource>(`${this.apiUrl}/projects/${projectId}/items/${itemId}/sources/${sourceId}`, this.normalizeDates(source), { params });
    }

    deleteProjectItemSource(projectId: number, itemId: number, sourceId: number, owningCompanyId: number): Observable<void> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.delete<void>(`${this.apiUrl}/projects/${projectId}/items/${itemId}/sources/${sourceId}`, { params });
    }

    getMaterialReservationRequests(owningCompanyId: number): Observable<PosmMaterialReservationRequest[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmMaterialReservationRequest[]>(`${this.apiUrl}/projects/material-reservation-requests`, { params });
    }

    reserveMaterialReservation(sourceId: number, owningCompanyId: number, request: PosmMaterialReservationCommand): Observable<PosmMaterialReservationRequest> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmMaterialReservationRequest>(
            `${this.apiUrl}/projects/material-reservation-requests/${sourceId}/reservations`,
            this.normalizeDates(request),
            { params }
        );
    }

    releaseMaterialReservation(sourceId: number, reservationId: number, owningCompanyId: number): Observable<PosmMaterialReservationRequest> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.delete<PosmMaterialReservationRequest>(
            `${this.apiUrl}/projects/material-reservation-requests/${sourceId}/reservations/${reservationId}`,
            { params }
        );
    }

    reserveProjectItemSourceStock(projectId: number, itemId: number, sourceId: number, owningCompanyId: number, request: PosmStockReservationRequest): Observable<PosmStockReservation> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmStockReservation>(`${this.apiUrl}/projects/${projectId}/items/${itemId}/sources/${sourceId}/reserve-stock`, request, { params });
    }

    getClosureStatus(projectId: number, owningCompanyId: number): Observable<PosmClosureStatus> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<PosmClosureStatus>(`${this.closureApiUrl}/projects/${projectId}/status`, { params });
    }

    generateCompletionReport(projectId: number, owningCompanyId: number, request: PosmGenerateCompletionReportRequest): Observable<PosmCompletionReport> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmCompletionReport>(`${this.closureApiUrl}/projects/${projectId}/completion-reports`, this.normalizeDates(request), { params });
    }

    exportCompletionReport(reportId: number, owningCompanyId: number, format: 'PDF' | 'PPTX'): Observable<PosmReportExport> {
        const params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('format', format);
        return this.http.post<PosmReportExport>(`${this.closureApiUrl}/reports/${reportId}/exports`, {}, { params });
    }

    downloadCompletionReportPdf(reportId: number, owningCompanyId: number): Observable<Blob> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get(`${this.closureApiUrl}/reports/${reportId}/pdf`, { params, responseType: 'blob' });
    }

    downloadCompletionReportPptx(reportId: number, owningCompanyId: number): Observable<Blob> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get(`${this.closureApiUrl}/reports/${reportId}/pptx`, { params, responseType: 'blob' });
    }

    closeProject(projectId: number, owningCompanyId: number, request: PosmCloseProjectRequest): Observable<PosmClosureStatus> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmClosureStatus>(`${this.closureApiUrl}/projects/${projectId}/close`, request, { params });
    }

    reopenProject(projectId: number, owningCompanyId: number, request: PosmCloseProjectRequest): Observable<PosmClosureStatus> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmClosureStatus>(`${this.closureApiUrl}/projects/${projectId}/reopen`, request, { params });
    }

    shareClientTracking(projectId: number, owningCompanyId: number, request: PosmShareClientTrackingRequest): Observable<PosmClientTrackingShare> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<PosmClientTrackingShare>(`${this.closureApiUrl}/projects/${projectId}/client-tracking-shares`, this.normalizeDates(request), { params });
    }

    disableClientTrackingShare(shareId: number, owningCompanyId: number, actorCustomerId?: number): Observable<PosmClientTrackingShare> {
        let params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        if (actorCustomerId) params = params.set('actorCustomerId', actorCustomerId.toString());
        return this.http.post<PosmClientTrackingShare>(`${this.closureApiUrl}/client-tracking-shares/${shareId}/disable`, {}, { params });
    }

    getClientTracking(token: string): Observable<PosmClientTrackingView> {
        return this.http.get<PosmClientTrackingView>(`${this.closureApiUrl}/client-tracking/${token}`);
    }

    private normalizeDates<T>(payload: T): T {
        return JSON.parse(JSON.stringify(payload, (_key, value) => {
            if (value instanceof Date) {
                const offset = value.getTimezoneOffset();
                const local = new Date(value.getTime() - offset * 60_000);
                return local.toISOString().slice(0, 10);
            }
            return value;
        }));
    }

    private appendDeliveryScheduleFilters(params: HttpParams, filters?: PosmDeliveryScheduleFilterCriteria): HttpParams {
        if (!filters) {
            return params;
        }
        const filterEntries: Array<[keyof PosmDeliveryScheduleFilterCriteria, string | string[] | null | undefined]> = [
            ['retailer', filters.retailer],
            ['store', filters.store],
            ['city', filters.city],
            ['item', filters.item ?? null],
            ['supplier', filters.supplier],
            ['readiness', filters.readiness],
            ['dueStatus', filters.dueStatus],
            ['progressStage', filters.progressStage],
            ['releaseStatus', filters.releaseStatus],
            ['shipmentStatus', filters.shipmentStatus],
            ['truck', filters.truck],
            ['driver', filters.driver]
        ];
        for (const [key, value] of filterEntries) {
            const text = Array.isArray(value)
                ? value.map(item => `${item || ''}`.trim()).filter(Boolean).join(',')
                : `${value || ''}`.trim();
            if (text) {
                params = params.set(key, text);
            }
        }
        return params;
    }
}

